import { memo, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  FaIdBadge,
  FaUserCheck,
  FaEnvelope,
  FaPhone,
  FaLanguage,
  FaVenusMars,
  FaBirthdayCake,
  FaGraduationCap,
  FaBook,
  FaLightbulb,
  FaChalkboardTeacher,
  FaHandsHelping,
  FaUsers,
  FaLayerGroup,
  FaTicketAlt,
} from "react-icons/fa";
import { User } from "../../types/Types";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { isPaidPortalMember } from "../../utils/membershipTier";

interface YouthProfileProps {
  userData: User;
}

const YouthProfile = memo(({ userData }: YouthProfileProps) => {
  const { t, i18n } = useTranslation();
  const np = () => t("account.notProvided");
  const dynamicFields = {
    aboutYou: userData.aboutYou,
    email: userData.email,
    phoneNumber: userData.phoneNumber,
    languagePreference: userData.languagePreference,
    gender: userData.gender,
    educationLevel: userData.educationLevel,
    studyInterest: userData.studyInterest,
    mentorshipObjectives: userData.mentorshipObjectives,
    mentorshipExpertise: userData.mentorshipExpertise,
    mentorshipFormat: userData.mentorshipFormat,
    platformInterest: userData.platformInterest,
    peerNetworking: userData.peerNetworking,
    membershipName: userData.membershipName,
    referralCode: userData.referralCode,
    referralCodeUsed: userData.referralCodeUsed,
  };
  const { data: translated } = useTranslateDynamicFields(
    dynamicFields,
    i18n.resolvedLanguage || i18n.language
  );
  const tx = (key: keyof typeof dynamicFields, fallback?: string | null) =>
    translated?.[key] || fallback || np();
  const showMembershipFields = isPaidPortalMember(userData);

  const formattedDateOfBirth = useMemo(
    () =>
      userData.dateOfBirth
        ? new Date(userData.dateOfBirth).toLocaleDateString(
            i18n.resolvedLanguage || i18n.language || "en"
          )
        : "",
    [i18n.language, i18n.resolvedLanguage, userData.dateOfBirth]
  );

  return (
    <div dir="ltr">
      {/* About */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 bg-project-gradient text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.about")}</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col className="mb-3">
            <div className="d-flex align-items-start">
              <div className="text-muted small">
                {tx("aboutYou", userData.aboutYou)}
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      {/* Personal Details */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 bg-project-gradient text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.personalDetails")}</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaEnvelope size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.email")}</strong>
                <div className="text-muted small">
                  {tx("email", userData.email)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaPhone size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.contactNumber")}</strong>
                <div className="text-muted small">
                  {tx("phoneNumber", userData.phoneNumber)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLanguage size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.languagePreference")}</strong>
                <div className="text-muted small">
                  {tx("languagePreference", userData.languagePreference)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaVenusMars size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.gender")}</strong>
                <div className="text-muted small">
                  {tx("gender", userData.gender)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBirthdayCake size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.dateOfBirth")}</strong>
                <div className="text-muted small">
                  {formattedDateOfBirth || np()}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      {/* Education */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 bg-project-gradient text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.education")}</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaGraduationCap size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.educationLevel")}</strong>
                <div className="text-muted small">
                  {tx("educationLevel", userData.educationLevel)}
                </div>
              </div>
            </div>
          </Col>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBook size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.fieldOfStudyInterest")}</strong>
                <div className="text-muted small">
                  {tx("studyInterest", userData.studyInterest)}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>

      {/* Interests */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 bg-project-gradient text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.interests")}</h5>
        </div>
        <div className="p-3">
        <Row>
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLightbulb size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.mentorshipObjectives")}</strong>
                <div className="text-muted small">
                  {tx("mentorshipObjectives", userData.mentorshipObjectives)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaChalkboardTeacher
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div>
                <strong>{t("account.field.preferredMentorshipExpertise")}</strong>
                <div className="text-muted small">
                  {tx("mentorshipExpertise", userData.mentorshipExpertise)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaHandsHelping size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.preferredMentorshipFormat")}</strong>
                <div className="text-muted small">
                  {tx("mentorshipFormat", userData.mentorshipFormat)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLayerGroup size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.areasOfInterestPlatform")}</strong>
                <div className="text-muted small">
                  {tx("platformInterest", userData.platformInterest)}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaUsers size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.interestedPeerNetworking")}</strong>
                <div className="text-muted small">
                  {tx("peerNetworking", userData.peerNetworking)}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        </div>
      </div>
      {/* Membership Details */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 bg-project-gradient text-white align-items-center rounded-top">
          <h5 className="mb-0">{t("account.section.membershipDetails")}</h5>
        </div>
        <div className="p-3">
        <Row>
          {showMembershipFields && (
            <>
              <Col md={4} className="mb-3">
                <div className="d-flex align-items-start">
                  <FaIdBadge size={20} className="me-2 mt-1 text-secondary" />
                  <div>
                    <strong>{t("account.field.membershipName")}</strong>
                    <div className="text-muted small">
                      {tx("membershipName", userData.membershipName)}
                    </div>
                  </div>
                </div>
              </Col>
              <Col md={4} className="mb-3">
                <div className="d-flex align-items-start">
                  <FaUserCheck size={20} className="me-2 mt-1 text-secondary" />
                  <div>
                    <strong>{t("account.field.membershipType")}</strong>
                    <div className="text-muted small">
                      {userData.isAnnualMembership
                        ? t("account.billingCycle.annual")
                        : t("account.billingCycle.monthly")}
                    </div>
                  </div>
                </div>
              </Col>
            </>
          )}
          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaTicketAlt size={20} className="me-2 mt-1 text-secondary" />
              <div>
                <strong>{t("account.field.referralCode")}</strong>
                <div className="text-muted small">
                  {tx("referralCode", userData.referralCode)}
                </div>
              </div>
            </div>
          </Col>
          {userData.referralCodeUsed && (
            <Col md={4} className="mb-3">
              <div className="d-flex align-items-start">
                <FaTicketAlt size={20} className="me-2 mt-1 text-secondary" />
                <div>
                  <strong>{t("account.field.referralCodeUsed")}</strong>
                  <div className="text-muted small">
                    {tx("referralCodeUsed", userData.referralCodeUsed)}
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>
        </div>
      </div>
    </div>
  );
});
YouthProfile.displayName = "YouthProfile";

export default YouthProfile;
